import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface NudgeControlsProps {
  onNudge: (direction: 'up' | 'down' | 'left' | 'right', amount: number) => void;
  onCenter: () => void;
  stepSize: number;
  setStepSize: (size: number) => void;
}

export const NudgeControls = ({
  onNudge,
  onCenter,
  stepSize,
  setStepSize
}: NudgeControlsProps) => {
  const stepSizes = [1, 5, 10];

  return (
    <TooltipProvider>
      <div className="p-4 border rounded-lg bg-muted/50 space-y-3">
        <h4 className="text-sm font-medium">Verplaatsen</h4>
        
        {/* Arrow buttons grid */}
        <div className="grid grid-cols-3 gap-1 w-fit mx-auto">
          <div /> {/* empty cell */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onNudge('up', stepSize)}
                className="h-8 w-8 p-0"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Omhoog ({stepSize}px)</p>
            </TooltipContent>
          </Tooltip>
          <div /> {/* empty cell */}
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onNudge('left', stepSize)}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Links ({stepSize}px)</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={onCenter}
                className="h-8 w-8 p-0"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Centreer op pagina</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onNudge('right', stepSize)}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Rechts ({stepSize}px)</p>
            </TooltipContent>
          </Tooltip>
          
          <div /> {/* empty cell */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onNudge('down', stepSize)}
                className="h-8 w-8 p-0"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Omlaag ({stepSize}px)</p>
            </TooltipContent>
          </Tooltip>
          <div /> {/* empty cell */}
        </div>
        
        {/* Step size selector */}
        <div className="flex gap-2 justify-center flex-wrap">
          {stepSizes.map(size => (
            <Badge
              key={size}
              variant={stepSize === size ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setStepSize(size)}
            >
              {size}px
            </Badge>
          ))}
        </div>
        
        <p className="text-xs text-muted-foreground text-center">
          Of gebruik <kbd className="px-1 py-0.5 bg-background rounded text-[10px] font-mono border">←</kbd>{' '}
          <kbd className="px-1 py-0.5 bg-background rounded text-[10px] font-mono border">↑</kbd>{' '}
          <kbd className="px-1 py-0.5 bg-background rounded text-[10px] font-mono border">→</kbd>{' '}
          <kbd className="px-1 py-0.5 bg-background rounded text-[10px] font-mono border">↓</kbd>
        </p>
      </div>
    </TooltipProvider>
  );
};
